import React, {useEffect, useMemo, useState} from "react";
import B2BApiService from "../../../B2bApiService";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box, Checkbox, Chip, Divider, FormControlLabel,
    Grid, Switch,
    TextField,
    Typography,
    Collapse,
    IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import { t } from "../../../content";

const ControlPanel = ({
    selectedCandidates,
    setSelectedCandidates,
    groupA,
    setGroupA,
    groupB,
    setGroupB,
    groupEditedFlag,
    setGroupEditedFlag,
    runTeamSimulation,
    fetchDataErrorMessage,
    setFetchDataErrorMessage
}) => {
    const [showTwoGroups, setShowTwoGroups] = useState(false);
    const [poolOptions, setPoolOptions] = useState([])
    const [teamOptions, setTeamOptions] = useState([])
    const [showDescription, setShowDescription] = useState(false); // New state for showing the description

    const handleToggleDescription = () => {
        setShowDescription((prev) => !prev);
    };

    const handleDelete = (candidateToDelete) => {
        setSelectedCandidates((prev) =>
            prev.filter((candidate) => candidate.id !== candidateToDelete.id)
        );

        // Remove candidate from both groups if present
        setGroupA((prev) => prev.filter((candidate) => candidate.id !== candidateToDelete.id));
        setGroupB((prev) => prev.filter((candidate) => candidate.id !== candidateToDelete.id));
        setGroupEditedFlag(true);
        setFetchDataErrorMessage(''); // Clear any existing error messages when a candidate is removed
    };

    const handleCheckboxChange = (event, candidate) => {
        if (event.target.checked) {
            setSelectedCandidates((prev) => [
                ...prev,
                { ...candidate }, // Include the entire candidate object
            ]);
            setGroupA((prev) => [
                ...prev,
                { ...candidate }, // Include the entire candidate object
            ]);
        } else {
            setSelectedCandidates((prev) =>
                prev.filter((c) => c.id !== candidate.id)
            );

            setGroupA((prev) =>
                prev.filter((c) => c.id !== candidate.id)
            );
        }
        setGroupEditedFlag(true);
        setFetchDataErrorMessage(''); // Clear any existing error messages when a candidate is added or removed
    };

    const isChecked = (candidate) => {
        return selectedCandidates.some((c) => c.id === candidate.id);
    };

    const handleToggleGroups = () => {
        setShowTwoGroups((prev) => !prev);
        setGroupA(selectedCandidates);
        setGroupB([]);
        setGroupEditedFlag(true);
        setFetchDataErrorMessage(''); // Clear any existing error messages when toggling groups
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        // Determine the source and destination groups
        const sourceGroup = result.source.droppableId === "groupA" ? groupA : groupB;
        const destinationGroup = result.destination.droppableId === "groupA" ? groupA : groupB;

        // Clone the source group and remove the dragged item
        const sourceItems = Array.from(sourceGroup);
        const [movedItem] = sourceItems.splice(result.source.index, 1);

        // Clone the destination group and add the dragged item
        const destinationItems = Array.from(destinationGroup);
        destinationItems.splice(result.destination.index, 0, movedItem);

        // Update the state for both groups
        if (result.source.droppableId === "groupA") {
            setGroupA(sourceItems);
            setGroupB(destinationItems);
        } else {
            setGroupA(destinationItems);
            setGroupB(sourceItems);
        }
        setGroupEditedFlag(true);
        setFetchDataErrorMessage(''); // Clear any existing error messages when dragging and dropping candidates
    };

    const handleRunSimulationClick = () => {
        if (groupA.length === 0 || groupB.length === 0) {
            // If one of the groups is empty, move all candidates to group one and close comparison mode
            setShowTwoGroups(false);
            setGroupA(selectedCandidates);
            setGroupB([]);
        }
        runTeamSimulation();
    }

    const b2bServiceObj = useMemo(() => {
        return new B2BApiService();
    },[]);

    useEffect(() => {
        b2bServiceObj.LoadPoolsUsersWithAllData((setPoolOptions))
        b2bServiceObj.LoadTeamMembersByCompanyId('', (setTeamOptions))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [b2bServiceObj])

    // If either group is empty or the total selected candidates are less than 2, disable comparison mode and move all to group one
    useEffect(() => {
        if (selectedCandidates.length < 2) {
            setShowTwoGroups(false);
            setGroupA(selectedCandidates);
            setGroupB([]);
        }
    }, [selectedCandidates, setGroupA, setGroupB]);

    return (
        <>
        <Box sx={{ padding: 3 }}>
            <Box >
                <Typography variant="h5" sx={{color:'#00B0A0'}}>
                    {t('simulator.title')}
                </Typography>
                <p className="s bold mar-15 top">
                    {t('simulator.intro')} <br/>
                    {t('simulator.introRest')}
                </p>
                <IconButton onClick={handleToggleDescription} sx={{ marginBottom : "6px" }}>
                    <InfoIcon sx={{ color: '#FFBB31' }} /> <span><p style={{fontSize: '14px',marginBottom: 0}}>{t('simulator.instruction')}</p></span>
                </IconButton>
            </Box>
            <Divider />
            <Collapse in={showDescription}>
                <Box sx={{ backgroundColor: '#F2FBFA', borderRadius: 2, padding: 2, }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: 'black', lineHeight: 1.6 }}>
                            {t('simulator.mixAndMatch')} <br/>
                            {t('simulator.mixAndMatchRest')}
                        </Typography>
                        <IconButton
                            aria-label={t('common.actions.close')}
                            color="inherit"
                            size="small"
                            onClick={handleToggleDescription}
                        >
                            <CloseIcon  sx={{color : 'red'}}/>
                        </IconButton>
                    </Box>
                    <Box sx={{ marginTop: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {t('simulator.helpfulTips')}
                        </Typography>
                        <Box component="ul" sx={{ marginTop: 0, paddingLeft: 2 }}>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                <CheckCircleIcon sx={{ color: '#00B0A0', marginRight: 1 }} />
                                <Typography variant="body2">{t('simulator.tipDropdowns')}</Typography>
                            </Box>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                <CheckCircleIcon sx={{ color: '#00B0A0', marginRight: 1 }} />
                                <Typography variant="body2">{t('simulator.tipSearch')}</Typography>
                            </Box>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                <CheckCircleIcon sx={{ color: '#00B0A0', marginRight: 1 }} />
                                <Typography variant="body2">{t('simulator.tipCheckbox')}</Typography>
                            </Box>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                <CheckCircleIcon sx={{ color: '#00B0A0', marginRight: 1 }} />
                                <Typography variant="body2">{t('simulator.tipCompare')}</Typography>
                            </Box>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                                <CheckCircleIcon sx={{ color: '#00B0A0', marginRight: 1 }} />
                                <Typography variant="body2">{t('simulator.tipDrag')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Collapse>
            {showDescription && <Divider sx={{ marginY: 3 }} />}

            <Grid container spacing={2} sx={{ display: 'flex', paddingTop:'20px'}}>
                <Grid item xs={12} md={5}>
                    <Typography variant="h6"><strong>{t('simulator.selectFromPools')}</strong></Typography>
                    <Autocomplete
                        options={[...new Set(poolOptions.map(candidate => candidate.pool_name))]} // Use pool_name from the new structure
                        filterOptions={(options, { inputValue }) => {
                            const lowercasedInput = inputValue.toLowerCase();
                            return options.filter(pool =>
                                pool.toLowerCase().includes(lowercasedInput) ||
                                poolOptions.some(candidate =>
                                    candidate.pool_name === pool &&
                                    `${candidate.first_name} ${candidate.last_name}`.toLowerCase().includes(lowercasedInput) // Filter by candidate names
                                )
                            );
                        }}
                        renderInput={(params) => <TextField style={{ backgroundColor: '#FFE4AD'}} {...params} label={t('simulator.selectFromPoolsLabel')} />}
                        renderOption={(props, pool) => {
                            const candidatesInPool = poolOptions.filter(candidate => candidate.pool_name === pool);
                            const candidateCount = candidatesInPool.length; // Get the number of candidates in the pool

                            return (
                                <Accordion key={pool}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>
                                            {pool} ({t(candidateCount === 1 ? 'simulator.candidateCount' : 'simulator.candidatesCount', { count: candidateCount })})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={1} padding={2}>
                                            {candidatesInPool.map(candidate => (
                                                <Grid item xs={12} sm={6} md={4} key={candidate.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isChecked(candidate)}
                                                                onChange={(e) => handleCheckboxChange(e, candidate)} // Checkbox state handling
                                                            />
                                                        }
                                                        label={
                                                            <span
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.preventDefault(); // Prevent dropdown from closing
                                                                    handleCheckboxChange({ target: { checked: !isChecked(candidate) } }, candidate); // Toggle checkbox
                                                                }}
                                                            >
                                                                {`${candidate.first} ${candidate.last}`}
                                                            </span>
                                                        }
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        }}
                    />


                    <Typography variant="h6" sx={{ marginTop: 2 }}><strong>{t('simulator.selectFromTeams')}</strong></Typography>
                    <Autocomplete
                        options={[...new Set(teamOptions.map(employee => employee.team_name))]} // Use team_name from the new structure
                        filterOptions={(options, { inputValue }) => {
                            const lowercasedInput = inputValue.toLowerCase();
                            return options.filter(team =>
                                team.toLowerCase().includes(lowercasedInput) ||
                                teamOptions.some(employee =>
                                    employee.team_name === team &&
                                    `${employee.first} ${employee.last}`.toLowerCase().includes(lowercasedInput) // Filter by employee names
                                )
                            );
                        }}
                        renderInput={(params) => <TextField style={{ backgroundColor: '#8FD3CB' }} {...params} label={t('simulator.selectFromTeamsLabel')} />}
                        renderOption={(props, team) => {
                            const employeesInPool = teamOptions.filter(employee => employee.team_name === team);
                            const employeeCount = employeesInPool.length; // Get the number of employees in the pool

                            return (
                                <Accordion key={team}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>
                                            {team} ({t(employeeCount === 1 ? 'simulator.employeeCount' : 'simulator.employeesCount', { count: employeeCount })})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={1} padding={2}>
                                            {employeesInPool.map(employee => (
                                                <Grid item xs={12} sm={6} md={4} key={employee.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={isChecked(employee)}
                                                                onChange={(e) => handleCheckboxChange(e, employee)} // Checkbox state handling
                                                            />
                                                        }
                                                        label={
                                                            <span
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.preventDefault(); // Prevent dropdown from closing
                                                                    handleCheckboxChange({ target: { checked: !isChecked(employee) } }, employee); // Toggle checkbox
                                                                }}
                                                            >
                                                                {`${employee.first} ${employee.last}`}
                                                            </span>
                                                        }
                                                        sx={{ display: 'flex', alignItems: 'center' }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        }}
                    />

                    <Box sx={{ marginTop: 3 }}>
                        <FormControlLabel
                            label={
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    {t('simulator.comparisonMode')}
                                </Typography>
                            }
                            control={
                                <Switch
                                    checked={showTwoGroups}
                                    onChange={handleToggleGroups}
                                    color="primary"
                                    disabled={selectedCandidates.length < 2}
                                />
                            }
                        />
                        <p className="s">{t('simulator.selectTwoToEnable')}</p>
                    </Box>
                </Grid>

                <Grid item xs={1} md={1}>
                    <Divider orientation="vertical" flexItem sx={{ height: '100%', margin: '0 16px' }} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Grid container spacing={2} sx={{ width: '100%' }}>
                                <Grid item xs={5} md={5}> {/* 45% width for Group A */}
                                    <Droppable droppableId="groupA">
                                        {(provided) => (
                                            <Box
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                sx={{ padding: 1, textAlign: 'center' }}
                                            >
                                                <Typography variant="h6" sx={{ width: '100%', textAlign: 'center', marginBottom: 2 }}>
                                                    <strong>{showTwoGroups ? t('simulator.selectedGroupA') : t('simulator.selectedGroup')}</strong>
                                                    {groupB?.length > 0 && (
                                                        <div className="grey" style={{ fontSize: '12px', width: '100%', height: '50px', border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {t('simulator.dragHere')}
                                                        </div>
                                                    )}
                                                </Typography>
                                                {groupA.map((candidate, index) => (
                                                    <Draggable key={candidate.id} draggableId={candidate.id.toString()} index={index}>
                                                        {(provided) => (
                                                            <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                <Chip
                                                                    label={`${candidate.name} (${candidate.pool_name ? candidate.pool_name : candidate.team_name})`}
                                                                    onDelete={() => handleDelete(candidate)}
                                                                    sx={{
                                                                        margin: '4px',
                                                                        backgroundColor: candidate.pool_name ? '#FFE4AD' : '#8FD3CB',
                                                                        color: '#575757'
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </Grid>

                                {showTwoGroups && <Grid item xs={2} md={2} sx={{ textAlign: 'center', alignSelf: 'center' }}>
                                    <Typography variant="h6">
                                        <strong>{t('simulator.vs')}</strong>
                                    </Typography>
                                </Grid>}

                                {showTwoGroups && <Grid item xs={5} md={5}> {/* 45% width for Group B */}
                                    <Droppable droppableId="groupB">
                                        {(provided) => (
                                            <Box
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                sx={{ padding: 1, textAlign: 'center' }}
                                            >
                                                <Typography variant="h6" sx={{ width: '100%', textAlign: 'center', marginBottom: 2 }}>
                                                    <strong>{t('simulator.selectedGroupB')}</strong>
                                                    {groupA?.length > 0 && (
                                                        <div className="grey" style={{ fontSize: '12px', width: '100%', height: '50px', border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {t('simulator.dragHere')}
                                                        </div>
                                                    )}
                                                </Typography>
                                                {groupB.map((candidate, index) => (
                                                    <Draggable key={candidate.id} draggableId={candidate.id.toString()} index={index}>
                                                        {(provided) => (
                                                            <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                                <Chip
                                                                    label={`${candidate.name} (${candidate.pool_name ? candidate.pool_name : candidate.team_name})`}
                                                                    onDelete={() => handleDelete(candidate)}
                                                                    sx={{
                                                                        margin: '4px',
                                                                        backgroundColor: candidate.pool_name ? '#FFE4AD' : '#8FD3CB',
                                                                        color: '#575757'
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </Box>
                                        )}
                                    </Droppable>
                                </Grid>}
                            </Grid>
                        </DragDropContext>
                    </Box>
                </Grid>

            </Grid>
        </Box>
        {groupEditedFlag && 
            <div className='center-content'>
                <button className="mazhrBtn default-green mar-25" onClick={() => handleRunSimulationClick()}>{t('simulator.runSimulation')}</button>
            </div>
        }
        {fetchDataErrorMessage && 
            <div className='center-content mar-25'>
                <p style={{ color: 'red' }} className="error-message">{fetchDataErrorMessage}</p>
            </div>
        }
        </>
    );
};

export default ControlPanel
